import { User } from 'oidc-client';
import {
  FlagId,
  Folder,
  Message,
  foldersWithSeparatedParticipant,
  MonitId,
  FolderMessage,
  ConversationMessage,
  FolderMessageContent,
  MessageParticipant,
  MessageInFolder,
} from '../../../models/communication.types';
import { SelectableTableColumn } from '../../SelectableTable/SelectableTable.types';
import MessageMonit from '../MessageMonit.component';
import { MessageTableItem, TableMessageParticipant } from './MessagesList.types';
import MessageCell from './MessageCell.component';
import MessageParticipantLabel from './MessageParticipantLabel.component';
import LinkIcon from '@mui/icons-material/Link';
import AttachmentIcon from '@mui/icons-material/Attachment';
import DateCell from './DateCell.component';
import PriorityMediumIcon from '../PriorityMediumIcon.component';
import PriorityHighIcon from '../PriorityHighIcon.component';
import { AssignFlag } from './AssignFlag.component';
import { removeMessageFlag, setMessageFlag } from '../../../services/communication/communication.service';
import { AlertType } from '../../../const/alerts';
import { getFunctionLabel } from '../../../pages/Assessment/Assessment.types';
import { fill } from '../../../utils/contrastSvgAttributes';

export const getMessageTableItem = (
  message: FolderMessage | ConversationMessage,
  smallerFont: boolean,
  boldUnread: boolean,
): MessageTableItem => {
  const isRead =
    !boldUnread || (message as FolderMessage).unreadMessages === 0 || (message as ConversationMessage).isRead === true;
  return {
    id: message.messageId,
    flag: <AssignFlag flagId={message.flag} />,
    flagId: message.flag,
    participant: (
      <MessageCell isRead={isRead} withEllipsis={true} smallerFont={smallerFont} isTooltip={true}>
        <MessageParticipantLabel message={message} isMessageContentView={false} />
      </MessageCell>
    ),
    subject: (
      <MessageCell isRead={isRead} withEllipsis={true} smallerFont={smallerFont} isTooltip={true}>
        {message.subject}
      </MessageCell>
    ),
    assessmentSession: (
      <MessageCell isRead={isRead} withEllipsis={true} smallerFont={smallerFont} isTooltip={true}>
        {message.assessmentSession ? message.assessmentSession : '-'}
      </MessageCell>
    ),
    monit: <MessageMonit message={message} />,
    sendDate: <DateCell isRead={isRead} message={message} smallerFont={smallerFont} />,
  };
};

export const getTableParticipant = (
  folder: Folder,
  user: User,
  message: Message | FolderMessageContent | null,
): TableMessageParticipant | null => {
  if (foldersWithSeparatedParticipant.includes(folder)) {
    return folder === Folder.Sent ? TableMessageParticipant.recipient : TableMessageParticipant.sender;
  } else {
    if (!message || !message.recipient) {
      return null;
    } else {
      return (Array.isArray(message.recipient) &&
        message.recipient.find((item: MessageParticipant) => user.profile.sub === item.userId)) ||
        (!Array.isArray(message.recipient) && user.profile.sub === message.recipient.userId)
        ? TableMessageParticipant.sender
        : TableMessageParticipant.recipient;
    }
  }
};

export const getMessageFlagLabel = (flagId: FlagId): { label: string; letter: string; color: string } => {
  switch (flagId) {
    case FlagId.Yellow:
      return { label: 'bardzo ważna', letter: 'B', color: '#FF9A02' };
    case FlagId.Red:
      return { label: 'pilna', letter: 'P', color: '#BC0000' };
    default:
      return { label: 'ważna', letter: 'W', color: '#347F42' };
  }
};

export const getMonitLabel = (monitId: MonitId): { label: string; icon: JSX.Element } => {
  switch (monitId) {
    case MonitId.hasAttachment:
      return {
        label: 'Załącznik',
        icon: <AttachmentIcon style={{ transform: 'rotate(320deg)', height: '16px', color: fill }} />,
      };
    case MonitId.hasLink:
      return { label: 'Link', icon: <LinkIcon style={{ height: '16px', color: fill }} /> };
    case MonitId.mediumPriority:
      return {
        label: 'Priorytet: ważny',
        icon: <PriorityMediumIcon />,
      };
    case MonitId.highPriority:
      return {
        label: 'Priorytet: wysoki',
        icon: <PriorityHighIcon />,
      };
    default:
      return { label: '???', icon: <></> };
  }
};

export const getParticipantDisplayName = (participant: MessageParticipant | MessageParticipant[]): string => {
  if (Array.isArray(participant)) {
    return participant.map((item) => getParticipantDisplayName(item)).join(', ');
  } else {
    if (participant?.userId && participant.userId !== null) {
      return `${participant.displayName} ${
        participant?.function !== undefined && participant.function !== null
          ? `(${getFunctionLabel(participant.function, participant?.isVerifier)})`
          : ''
      }`;
    } else {
      return participant?.function !== undefined && participant.function !== null
        ? getFunctionLabel(participant.function, participant?.isVerifier)
        : '';
    }
  }
};

export const compareFolderMessagesBySentDateAntichronologically = (a: Message, b: Message): number => {
  const date1 = new Date(a.sentDate).getTime();
  const date2 = new Date(b.sentDate).getTime();

  if (date1 < date2) {
    return 1;
  } else if (date1 > date2) {
    return -1;
  } else {
    return 0;
  }
};

export const compareMessagesInFolderBySentDateChronologically = (a: MessageInFolder, b: MessageInFolder): number => {
  return -compareFolderMessagesBySentDateAntichronologically(a.message, b.message); //minus because of inverted direction
};

export const handleSetMessageFlag = (
  activeMessageId: string | null,
  flagId: FlagId,
  handleUpdateCurrentMessageFlag: (flag: FlagId) => void,
  openAlert: (alert: AlertType) => void,
) => {
  if (activeMessageId) {
    setMessageFlag(activeMessageId, flagId).then(
      () => {
        handleUpdateCurrentMessageFlag(flagId);
      },
      () => {
        openAlert('send_error');
      },
    );
  }
};

export const handleRemoveMessageFlag = (
  activeMessageId: string | null,
  handleUpdateCurrentMessageFlag: (flag: FlagId | null) => void,
  openAlert: (alert: AlertType) => void,
) => {
  if (activeMessageId) {
    removeMessageFlag(activeMessageId).then(
      () => {
        handleUpdateCurrentMessageFlag(null);
      },
      () => {
        openAlert('delete_error');
      },
    );
  }
};
